import classNames from 'classnames'
import React from 'react'

import Image from '@src/quince-site-common/src/domains/Image'
import RichText from '@src/quince-site-common/src/domains/RichText'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { RichText as RichTextClass, RichTextProps } from '../../molecules'
import { Image as ImageClass, ImageProps } from '../image'
import Link from '../link'
import Swiper from '../swiper'

import styles from './productComparison.module.scss'

export interface ProductComparisonProductProps {
  title: string
  titleLink: string
  rows: RichText[]
  image: Image | undefined
  isCurrent?: boolean
}

export interface ProductComparisonTableProps {
  rowHeaders: RichText[]
  columns: ProductComparisonProductProps[]
  className?: string | undefined
}

const getImage = (image: Image | undefined): React.ReactNode => {
  if (!image) {
    return null
  }

  const imageProps: ImageProps = Image.makeImageProps(image)

  return <ImageClass {...imageProps} className={styles.productcomparisontable__column__cell__contentWrapper__image} />
}

const getColumnRichText = (columnText: RichText): React.ReactNode => {
  const columnRichTextProps: RichTextProps | undefined = RichText.makeRichTextProps(columnText)
  return columnText && columnRichTextProps && <RichTextClass {...columnRichTextProps} />
}

const getRowHeaders = (rowHeaders: RichText[]) => {
  return (
    <div className={classNames(styles.productcomparisontable__column, styles.productcomparisontable__rowHeaderColumn)}>
      <div
        className={classNames(
          styles.productcomparisontable__column__cell,
          styles.productcomparisontable__rowHeaderColumn__cell,
          styles.productcomparisontable__rowHeaderColumn__cell__first,
        )}
      />
      {rowHeaders.map((rowHeader, rowIndex) => (
        <div
          key={rowHeader.id}
          className={classNames(
            styles.productcomparisontable__column__cell,
            styles.productcomparisontable__rowHeaderColumn__cell,
            rowIndex === rowHeaders.length - 1 && styles.productcomparisontable__column__cell__last,
          )}
        >
          {getColumnRichText(rowHeader)}
        </div>
      ))}
    </div>
  )
}

const getProductColumn = (column: ProductComparisonProductProps, colIdx: number) => {
  return (
    <div
      key={column.title}
      className={classNames(
        styles.productcomparisontable__column,
        column.isCurrent && styles.productcomparisontable__column__currentcolumn,
      )}
    >
      <div
        className={classNames(
          styles.productcomparisontable__column__cell,
          styles.productcomparisontable__column__header,
          column.isCurrent && styles.productcomparisontable__column__current__cell,
          column.isCurrent && styles.productcomparisontable__column__current__header,
        )}
      >
        {column.isCurrent ? (
          <div>{column.title}</div>
        ) : (
          <Link
            href={column.titleLink}
            trackConfig={{
              trackGA: true,
              trackInternal: true,
              category: 'navigation',
              action: 'click_product_comparison_section_link',
              label: 'product_comparison_section__link',
              value: column.titleLink,
              context: {
                index: colIdx,
              },
            }}
          >
            <span className={styles.productcomparisontable__column__header__headerButton}>{column.title}</span>
          </Link>
        )}
      </div>
      {column.rows.map((rowText, index) => {
        return (
          <div
            key={rowText.id}
            className={classNames(
              styles.productcomparisontable__column__cell,
              column.isCurrent && styles.productcomparisontable__column__current__cell,
              index === column.rows.length - 1 && styles.productcomparisontable__column__cell__last,
            )}
          >
            <div className={styles.productcomparisontable__column__cell__contentWrapper}>
              {index === 1 ? getImage(column.image) : null}
              {getColumnRichText(rowText)}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const getProductColumns = (productColumns: ProductComparisonProductProps[]) => {
  return productColumns.map(getProductColumn)
}

const ProductComparisonTable = ({ rowHeaders, columns, className }: ProductComparisonTableProps) => {
  const classes = classNames(styles.wrapper, className)
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const [firstColumn] = columns.filter(column => !!column.isCurrent)
  const otherColumns = columns.filter(column => !column.isCurrent)

  return (
    <div className={classes}>
      <div className={styles.productcomparisontable}>
        <div
          className={classNames(
            styles.productcomparisontable_leftsection,
            !firstColumn && styles.productcomparisontable_leftsection__noFirstColumn,
          )}
        >
          {getRowHeaders(rowHeaders)}
          {firstColumn && getProductColumn(firstColumn)}
        </div>
        <div
          className={classNames(
            styles.productcomparisontable_rightsection,
            !firstColumn && styles.productcomparisontable_rightsection__noFirstColumn,
          )}
        >
          {isDesktop ? (
            <Swiper
              id="product-comparision-table-carousel"
              variableWidth
              showNavigator
              numberOfVisibleSlides={5}
              tracker="product-comparision-section-control"
            >
              {getProductColumns(otherColumns)}
            </Swiper>
          ) : (
            getProductColumns(otherColumns)
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductComparisonTable
